import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchRealEstateSourceRuns,
  pollRunUntilComplete,
  runRealEstateSource,
  updateRealEstateSource,
} from "../../../../services/realEstateSourceControls";
import RealEstateRunDetailModal from "./RealEstateRunDetailModal";
import RealEstateRunsModal from "./RealEstateRunsModal";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import DataSourceScheduleModal from "../dataSources/DataSourceScheduleModal";
import { mapRealEstateSourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";

function normalizeHealth(source) {
  if (!source.enabled) {
    return "disabled";
  }

  const latestStatus = source.latestRun?.status;

  if (
    source.isRunning ||
    source.health === "running" ||
    latestStatus === "RUNNING" ||
    latestStatus === "QUEUED" ||
    source.lastStatus === "RUNNING" ||
    source.lastStatus === "QUEUED"
  ) {
    return latestStatus === "QUEUED" ? "queued" : "running";
  }

  if (latestStatus === "FAILED" || source.health === "failed") {
    return "failed";
  }

  if (latestStatus === "SUCCESS" && source.health === "healthy") {
    return "healthy";
  }

  if (latestStatus === "SUCCESS" && source.health === "warning") {
    return "warning";
  }

  return source.health || (source.latestRun ? "warning" : "never_run");
}

const RealEstateSourceControlsTable = ({
  sources,
  filters,
  onRefresh,
  showToast,
  setSources,
  hideEmpty = false,
}) => {
  const [busyKey, setBusyKey] = useState(null);
  const [scheduleSource, setScheduleSource] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [runsSource, setRunsSource] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState("");
  const [detailRunId, setDetailRunId] = useState(null);
  const [detailSource, setDetailSource] = useState(null);
  const pollAbortRef = useRef(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.displayName?.toLowerCase().includes(query) ||
          source.sourceKey?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.enabled);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.enabled);
    }

    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => normalizeHealth(source) === filters.statusFilter);
    }

    return list
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map((source) => mapRealEstateSourceToGridRow(source));
  }, [sources, filters]);

  const patchSourceInList = (updated) => {
    if (!updated?.sourceKey) {
      return;
    }

    setSources((current) =>
      current.map((item) => (item.sourceKey === updated.sourceKey ? { ...item, ...updated } : item))
    );
  };

  const handleToggleEnabled = async (source) => {
    const nextEnabled = !source.enabled;
    setBusyKey(source.sourceKey);

    setSources((current) =>
      current.map((item) =>
        item.sourceKey === source.sourceKey
          ? {
              ...item,
              enabled: nextEnabled,
              health: nextEnabled ? item.health : "disabled",
            }
          : item
      )
    );

    try {
      const updated = await updateRealEstateSource(source.sourceKey, { enabled: nextEnabled });
      patchSourceInList(updated);
      showToast(
        nextEnabled ? `${source.displayName} enabled.` : `${source.displayName} disabled.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      setSources((current) =>
        current.map((item) =>
          item.sourceKey === source.sourceKey ? { ...item, enabled: source.enabled } : item
        )
      );
      showToast(error.message || "Unable to update source.", "error");
    } finally {
      setBusyKey(null);
    }
  };

  const handleRunNow = async (source) => {
    setBusyKey(source.sourceKey);
    pollAbortRef.current?.abort();
    pollAbortRef.current = new AbortController();

    try {
      const result = await runRealEstateSource(source.sourceKey);
      const runId = result.runId;

      showToast(
        result.message || `Sync started for ${source.displayName}.`,
        "success"
      );

      setSources((current) =>
        current.map((item) =>
          item.sourceKey === source.sourceKey
            ? {
                ...item,
                isRunning: true,
                health: "running",
                lastStatus: result.status || "QUEUED",
                activeRunId: runId,
                latestRun: {
                  ...(item.latestRun || {}),
                  runId,
                  status: result.status || "QUEUED",
                },
              }
            : item
        )
      );

      if (runId) {
        const finalRun = await pollRunUntilComplete(runId, {
          signal: pollAbortRef.current.signal,
          onUpdate: (run) => {
            setSources((current) =>
              current.map((item) =>
                item.sourceKey === source.sourceKey
                  ? {
                      ...item,
                      isRunning: run.status === "RUNNING" || run.status === "QUEUED",
                      health: run.status === "FAILED" ? "failed" : "running",
                      lastStatus: run.status,
                      latestRun: run,
                    }
                  : item
              )
            );
          },
        });

        const tone = finalRun.status === "FAILED" ? "error" : "success";
        showToast(
          finalRun.status === "FAILED"
            ? `Sync failed for ${source.displayName}.`
            : `Sync completed for ${source.displayName}.`,
          tone
        );
      }

      onRefresh?.({ silent: true });
    } catch (error) {
      const message = error.message || `Unable to start sync for ${source.displayName}.`;
      showToast(message, "error");
      onRefresh?.({ silent: true });
    } finally {
      setBusyKey(null);
    }
  };

  const handleSaveSchedule = async (source, payload) => {
    setScheduleSaving(true);

    try {
      const updated = await updateRealEstateSource(source.sourceKey, payload);
      patchSourceInList(updated);
      setScheduleSource(null);
      showToast(`Schedule updated for ${source.displayName}.`, "success");
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(error.message || "Unable to save schedule.", "error");
      throw error;
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleViewRuns = async (source) => {
    setRunsSource(source);
    setRunsLoading(true);
    setRunsError("");

    try {
      const data = await fetchRealEstateSourceRuns(source.sourceKey, { limit: 25 });
      setRuns(data.runs || []);
    } catch (error) {
      setRuns([]);
      setRunsError(error.message || "Unable to load run history.");
    } finally {
      setRunsLoading(false);
    }
  };

  if (!filteredSources.length) {
    if (hideEmpty) {
      return null;
    }
    return (
      <p className="muted">
        {sources?.length
          ? "No sources match the current filters."
          : "No real estate sources are registered yet."}
      </p>
    );
  }

  return (
    <>
      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage={
          sources?.length
            ? "No sources match the current filters."
            : "No real estate sources are registered yet."
        }
        busyKey={busyKey}
        canManageSources={canManageSources}
        onToggleEnabled={handleToggleEnabled}
        onRunNow={handleRunNow}
        onSaveSchedule={handleSaveSchedule}
        onViewRuns={handleViewRuns}
        sectionLabel="Real estate"
        deleteDomain="real_estate"
        onDeleted={() => onRefresh?.()}
        onRefresh={onRefresh}
        showToast={showToast}
      />

      <DataSourceScheduleModal
        source={scheduleSource}
        isOpen={Boolean(scheduleSource)}
        isSaving={scheduleSaving}
        onClose={() => setScheduleSource(null)}
        onSave={handleSaveSchedule}
      />

      <RealEstateRunsModal
        source={runsSource}
        runs={runs}
        isOpen={Boolean(runsSource)}
        isLoading={runsLoading}
        errorMessage={runsError}
        onClose={() => {
          setRunsSource(null);
          setRuns([]);
        }}
        onViewDetails={(run) => {
          setDetailSource(runsSource);
          setDetailRunId(run.runId);
        }}
      />

      <RealEstateRunDetailModal
        runId={detailRunId}
        source={detailSource}
        isOpen={Boolean(detailRunId)}
        onClose={() => {
          setDetailRunId(null);
          setDetailSource(null);
        }}
      />
    </>
  );
};

export default RealEstateSourceControlsTable;
